import { useEffect, useCallback, useState } from 'react';

// Type definitions for Electron APIs
declare global {
  interface Window {
    electronAPI?: {
      navigateTo: (path: string) => void;
      showNotification: (options: {
        title: string;
        body: string;
        icon?: string;
      }) => Promise<void>;
      saveFile: (
        data: string,
        filename: string,
        filters?: Array<{ name: string; extensions: string[] }>
      ) => Promise<string | null>;
      onMenuAction: (callback: (event: any, action: string) => void) => void;
      onExportPortfolio: (callback: (event: any, filePath: string) => void) => void;
      windowControl: (action: 'minimize' | 'maximize' | 'close') => Promise<void>;
      toggleAlwaysOnTop: () => Promise<boolean>;
      toggleDevTools: () => Promise<void>;
      showAbout: () => Promise<void>;
      platform: string;
      isLiquidGlassEnabled?: boolean;
      getVersion: () => Promise<string>;
      removeAllListeners: (channel: string) => void;
    };
    systemAPI?: {
      platform: string;
      arch: string;
    };
    electronDevAPI?: {
      log: (...args: any[]) => void;
      error: (...args: any[]) => void;
    };
  }
}

export interface UseElectronReturn {
  isElectron: boolean;
  isLiquidGlassEnabled: boolean;
  platform: string | null;
  showPriceAlert: (symbol: string, price: number, message: string) => Promise<void>;
  exportPortfolioData: (data: any) => Promise<string | null>;
  exportTradeHistory: (trades: any[]) => Promise<string | null>;
  navigateToPage: (path: string) => void;
  isDesktopPlatform: boolean;
}

/**
 * Custom hook for Electron desktop integration
 * Provides safe access to desktop features for the trading platform
 */
export const useElectron = (): UseElectronReturn => {
  const [isElectron, setIsElectron] = useState(false);
  const [isLiquidGlassEnabled, setIsLiquidGlassEnabled] = useState(false);
  const [platform, setPlatform] = useState<string | null>(null);

  useEffect(() => {
    // Check if running in Electron
    const isElectronEnv = typeof window !== 'undefined' && window.electronAPI;
    setIsElectron(!!isElectronEnv);
    
    if (isElectronEnv) {
      setPlatform(window.electronAPI?.platform || null);
      setIsLiquidGlassEnabled(Boolean(window.electronAPI?.isLiquidGlassEnabled));
      
      // Setup menu action listeners
      window.electronAPI?.onMenuAction((event, action) => {
        console.log('Menu action received:', action);
        // Handle menu actions here
        switch (action) {
          case 'new-chart':
            window.electronAPI?.navigateTo('/chart');
            break;
          case 'new-chat':
            // baby: Main menu also navigates in main process; this covers pages that start a fresh chat in-app
            window.electronAPI?.navigateTo('/ningenie');
            break;
          default:
            break;
        }
      });

      // Setup portfolio export listener
      window.electronAPI?.onExportPortfolio((event, filePath) => {
        console.log('Export portfolio to:', filePath);
        // Trigger portfolio export
      });
    }

    return () => {
      // Cleanup listeners
      if (window.electronAPI) {
        window.electronAPI.removeAllListeners('menu-action');
        window.electronAPI.removeAllListeners('export-portfolio');
      }
    };
  }, []);

  /**
   * Show native price alert notification
   */
  const showPriceAlert = useCallback(async (symbol: string, price: number, message: string) => {
    if (!isElectron || !window.electronAPI) {
      // Fallback to browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`${symbol} Price Alert`, {
          body: `${message}\nPrice: $${price}`,
          icon: '/img/logo/nin_logo.png'
        });
      }
      return;
    }

    try {
      await window.electronAPI.showNotification({
        title: `${symbol} Price Alert`,
        body: `${message}\nPrice: $${price.toFixed(2)}`,
        icon: '/img/logo/nin_logo.png'
      });
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }, [isElectron]);

  /**
   * Export portfolio data to file
   */
  const exportPortfolioData = useCallback(async (data: any) => {
    if (!isElectron || !window.electronAPI) {
      // Fallback to browser download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nin-portfolio.json';
      a.click();
      URL.revokeObjectURL(url);
      return null;
    }

    try {
      const jsonData = JSON.stringify(data, null, 2);
      const filePath = await window.electronAPI.saveFile(
        jsonData,
        'nin-portfolio.json',
        [
          { name: 'JSON Files', extensions: ['json'] },
          { name: 'CSV Files', extensions: ['csv'] }
        ]
      );
      return filePath;
    } catch (error) {
      console.error('Failed to export portfolio:', error);
      return null;
    }
  }, [isElectron]);

  /**
   * Export trade history to CSV
   */
  const exportTradeHistory = useCallback(async (trades: any[]) => {
    if (!isElectron || !window.electronAPI) {
      // Fallback to browser download
      const csvContent = convertTradesToCSV(trades);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nin-trades.csv';
      a.click();
      URL.revokeObjectURL(url);
      return null;
    }

    try {
      const csvData = convertTradesToCSV(trades);
      const filePath = await window.electronAPI.saveFile(
        csvData,
        'nin-trades.csv',
        [
          { name: 'CSV Files', extensions: ['csv'] },
          { name: 'Excel Files', extensions: ['xlsx'] }
        ]
      );
      return filePath;
    } catch (error) {
      console.error('Failed to export trades:', error);
      return null;
    }
  }, [isElectron]);

  /**
   * Navigate to page (works in both web and desktop)
   */
  const navigateToPage = useCallback((path: string) => {
    if (isElectron && window.electronAPI) {
      window.electronAPI.navigateTo(path);
    } else {
      // Fallback to router navigation
      window.location.href = path;
    }
  }, [isElectron]);

  const isDesktopPlatform = isElectron && (platform === 'darwin' || platform === 'win32');

  return {
    isElectron,
    isLiquidGlassEnabled,
    platform,
    showPriceAlert,
    exportPortfolioData,
    exportTradeHistory,
    navigateToPage,
    isDesktopPlatform
  };
};

/**
 * Helper function to convert trades array to CSV format
 */
function convertTradesToCSV(trades: any[]): string {
  if (!trades.length) return '';
  
  const headers = Object.keys(trades[0]);
  const csvRows = [
    headers.join(','),
    ...trades.map(trade => 
      headers.map(header => {
        const value = trade[header];
        // Escape quotes and wrap in quotes if contains comma
        return typeof value === 'string' && value.includes(',') 
          ? `"${value.replace(/"/g, '""')}"` 
          : value;
      }).join(',')
    )
  ];
  
  return csvRows.join('\n');
}

/**
 * Hook for development logging in Electron
 */
export const useElectronDev = () => {
  const log = useCallback((...args: any[]) => {
    if (typeof window !== 'undefined' && window.electronDevAPI) {
      window.electronDevAPI.log(...args);
    } else {
      console.log(...args);
    }
  }, []);

  const error = useCallback((...args: any[]) => {
    if (typeof window !== 'undefined' && window.electronDevAPI) {
      window.electronDevAPI.error(...args);
    } else {
      console.error(...args);
    }
  }, []);

  return { log, error };
}; 
